<template>
  <ConfirmDialog group="settings-draft" :baseZIndex="2400" />
  <Dialog
    v-model:visible="visibleProxy"
    modal
    position="top"
    appendTo="body"
    :baseZIndex="1200"
    :closable="!isBusy"
    :closeOnEscape="!isBusy"
    :dismissableMask="false"
    :draggable="false"
    class="settings-modal-dialog"
    :class="{ 'settings-modal-dialog--tags': activeSectionMeta?.key === 'tags' }"
    :pt="dialogPassThrough"
    @hide="onDialogHide"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div
          class="crc-form-dialog-header__icon bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
          aria-hidden="true"
        >
          <i class="pi pi-cog" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">Settings</div>
          <div class="crc-form-dialog-header__subtitle">Workspace administration · {{ activeSectionMeta?.label || 'Settings' }}</div>
        </div>
      </div>
    </template>

    <div v-if="availableSections.length" class="settings-modal__body">
      <aside class="settings-modal__sidebar settings-modal__sidebar--desktop">
        <div class="settings-modal__nav-search">
          <InputText v-model="sectionSearch" placeholder="Find a section..." aria-label="Find a settings section" class="w-full" />
        </div>
        <div class="settings-modal__nav-scroll">
          <nav class="settings-modal__nav" aria-label="Settings sections">
            <SettingsNavItem
              v-for="section in filteredSections"
              :key="section.key"
              :label="section.label"
              :icon="section.icon"
              :active="section.key === activeSection"
              @select="selectSection(section.key)"
              :disabled="isBusy"
            />
          </nav>
          <p v-if="!filteredSections.length" class="settings-modal__no-results">No matching sections.</p>
        </div>
        <div class="settings-modal__version" aria-label="Application version">
          Version {{ appVersionLabel }}
        </div>
      </aside>

      <div class="settings-modal__sidebar settings-modal__sidebar--mobile">
        <Select
          :model-value="activeSection"
          :options="availableSections"
          option-label="label"
          option-value="key"
          aria-label="Settings section"
          :disabled="isBusy"
          class="w-full"
          @update:model-value="selectSection"
        />
      </div>

      <main class="settings-modal__content">
        <component
          :is="activeSectionComponent"
          v-if="visibleProxy && activeSectionComponent && activeSectionMeta"
          :key="activeSectionMeta.key"
          :section="activeSectionMeta"
          @close-settings="requestClose"
        />
      </main>
    </div>

    <div v-else class="settings-modal__empty">
      <Message severity="warn" :closable="false">
        You do not have access to any administration sections.
      </Message>
    </div>

  </Dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import ConfirmDialog from 'primevue/confirmdialog';
import { provideSettingsDraftGuard } from '../composables/useSettingsDraftGuard';
import { matchesSearchText } from '@/utils/searchText';
import Dialog from 'primevue/dialog';
import Message from 'primevue/message';
import SettingsNavItem from './SettingsNavItem.vue';
import { crcFormDialogPtNoScroll } from '@/components/common/dialog/crcDialogPt';
import { useGlobalSettings } from '../composables/useGlobalSettings';
import { useSettingsPermissions } from '../composables/useSettingsPermissions';
import { formatAppVersionLabel } from '@/constants/appMeta';
import '../styles/settingsOrgSection.css';

defineOptions({ name: 'SettingsModal' });

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:visible']);

const { activeSection, close, setActiveSection } = useGlobalSettings();
const { availableSections, isSectionAvailable } = useSettingsPermissions();
const { isDirty, isBusy, requestLeave } = provideSettingsDraftGuard();
const sectionSearch = ref('');
const filteredSections = computed(() => availableSections.value.filter((section) =>
  matchesSearchText([section.label, section.subtitle], sectionSearch.value)
));
const requestClose = () => requestLeave(() => emit('update:visible', false));
const selectSection = (key) => {
  if (key === activeSection.value || !isSectionAvailable(key)) return;
  requestLeave(() => { if (isSectionAvailable(key)) setActiveSection(key); });
};

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => { if (value) emit('update:visible', true); else requestClose(); },
});

const dialogPassThrough = {
  ...crcFormDialogPtNoScroll,
  content: { class: '!p-0 flex flex-col min-h-0 overflow-hidden' },
};

const activeSectionMeta = computed(() => {
  const key = activeSection.value;
  if (key && isSectionAvailable(key)) {
    return availableSections.value.find((section) => section.key === key) || null;
  }
  return availableSections.value[0] || null;
});

const activeSectionComponent = computed(() => activeSectionMeta.value?.component || null);

const appVersionLabel = formatAppVersionLabel();

const syncActiveSection = () => {
  const available = availableSections.value;
  if (!available.length) {
    if (activeSection.value != null) {
      setActiveSection(null);
    }
    return;
  }

  const current = activeSection.value;
  if (current && available.some((section) => section.key === current)) {
    return;
  }

  setActiveSection(available[0].key);
};

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      sectionSearch.value = '';
      syncActiveSection();
    }
  }
);

watch(availableSections, () => {
  if (props.visible) {
    syncActiveSection();
  }
});

const onDialogHide = () => {
  close();
};
</script>

<style scoped>
.settings-modal__body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.settings-modal__sidebar {
  flex-shrink: 0;
  background: var(--crc-content-bg);
  border-right: 1px solid var(--crc-panel-border, var(--surface-border));
}

.settings-modal__sidebar--desktop {
  display: flex;
  flex-direction: column;
  width: 220px;
  min-height: 0;
  padding: 0;
  overflow: hidden;
}

.settings-modal__nav-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  padding: 0.75rem 0.75rem 0.375rem;
}

.settings-modal__sidebar--mobile {
  display: none;
  width: 100%;
  border-right: none;
  border-bottom: 1px solid var(--crc-panel-border, var(--surface-border));
  padding: 0.75rem 1rem;
  overflow-x: auto;
  overflow-y: hidden;
}

.settings-modal__nav {
  width: 100%;
}

.settings-modal__version {
  flex: 0 0 auto;
  padding: 0.625rem 1rem 0.75rem;
  border-top: 1px solid var(--crc-panel-border, var(--surface-border));
  font-size: 0.6875rem;
  line-height: 1.25;
  color: var(--crc-text-muted, var(--text-color-secondary));
  letter-spacing: 0.02em;
}

.settings-modal__footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  justify-content: space-between;
  padding: 0.75rem 1rem;
}

.settings-modal__version--mobile {
  display: none;
}

.settings-modal__nav-search { padding: 0.75rem 0.75rem 0; }
.settings-modal__nav-search :deep(input) { font-size: 0.8125rem; }
.settings-modal__no-results { padding: 0.75rem; color: var(--crc-text-muted); font-size: 0.8125rem; }
.settings-modal__save-status { display: flex; align-items: center; gap: 0.5rem; color: var(--crc-text-muted); font-size: 0.75rem; }

.settings-modal__content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  scrollbar-gutter: stable;
  background: var(--crc-bar-bg, var(--surface-card));
}

.settings-modal__empty {
  padding: 1.5rem;
}

@media (max-width: 960px) {
  .settings-modal__body {
    flex-direction: column;
  }

  .settings-modal__sidebar--desktop {
    display: none;
  }

  .settings-modal__sidebar--mobile {
    display: block;
  }

  .settings-modal__footer {
    display: flex;
  }
}
</style>

<style>
.settings-modal-dialog {
  display: flex;
  flex-direction: column;
  --settings-modal-width: min(1100px, calc(100vw - 32px));
  --settings-modal-height: min(760px, calc(100dvh - 48px));
  width: var(--settings-modal-width) !important;
  height: var(--settings-modal-height) !important;
  max-height: var(--settings-modal-height) !important;
}

.settings-modal-dialog.settings-modal-dialog--tags {
  --settings-modal-width: min(1460px, calc(100vw - 40px));
  --settings-modal-height: min(920px, calc(100dvh - 48px));
}

.settings-modal-dialog .p-dialog-content {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

.settings-modal-dialog .p-dialog-footer {
  display: block;
  padding: 0;
}

.settings-modal-dialog .settings-org-section__body {
  scrollbar-gutter: stable;
}

@media (max-width: 960px) {
  .settings-modal-dialog .p-dialog-footer {
    display: block;
  }
}
</style>

<style scoped>
@media (max-width: 575px) {
  .settings-modal__footer { flex-wrap: wrap; }
  .settings-modal__save-status { flex: 1; min-width: 0; }
}
</style>
